import { Deferred, usePage } from "@inertiajs/react";
import { format, parse } from "date-fns";
import { AlertCircle, CalendarClock, LoaderIcon } from "lucide-react";
import {
	type ComponentProps,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	BypassCheckRow,
	BypassChecksPanel,
} from "@/components/admin-portal/appointment/bypass-checks-panel";
import {
	MitraAssignmentReasonBox,
	orderMitraReason,
} from "@/components/admin-portal/appointment/mitra-assignment-reason";
import {
	type ScheduleMode,
	ScheduleModeToggle,
} from "@/components/admin-portal/scheduling/schedule-mode-toggle";
import { SchedulePicker } from "@/components/admin-portal/scheduling/schedule-picker";
import { useDateContext } from "@/components/providers/date-provider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { TherapistOption } from "@/hooks/admin-portal/appointment/use-appointment-utils";
import {
	useFormActionButtons,
	useRescheduleFields,
} from "@/hooks/admin-portal/appointment/use-reschedule-form";
import { getGenderIcon } from "@/hooks/use-gender";
import { isMitra } from "@/lib/appointments/form";
import {
	slotSelectionToDateTime,
	slotSelectionToTherapist,
	toSlotSelection,
} from "@/lib/therapist-scheduling/form-adapter";
import {
	buildReschedulePickerProps,
	resolveVisitAddress,
} from "@/lib/therapist-scheduling/reschedule-picker-props";
import type { SlotSelection } from "@/lib/therapist-scheduling/types";
import { cn } from "@/lib/utils";
import type { AppointmentRescheduleGlobalPageProps } from "@/pages/AdminPortal/Appointment/Reschedule";
import DateTimePicker from "./form/date-time";
import { SelectedTherapistCard } from "./form/selected-therapist-card";
import { TherapistSearchList } from "./form/therapist-search-list";

// Gates the calendar picker + its mode toggle. Same flag create booking reads.
const getUseNewSchedulePicker = (
	pageProps?: AppointmentRescheduleGlobalPageProps["props"],
) => {
	try {
		return (
			pageProps?.adminPortal?.featureFlags?.useNewSchedulePickerEnabled ?? false
		);
	} catch {
		return false;
	}
};

export interface FormActionButtonsprops extends ComponentProps<"div"> {
	isLoading: boolean;
}

export const FormActionButtons = memo(function Component({
	className,
	isLoading,
}: FormActionButtonsprops) {
	const { t: taf } = useTranslation("appointments-form");
	const { isDekstop, onBackRoute } = useFormActionButtons();

	return (
		<div
			className={cn(
				"flex flex-col w-full gap-4 lg:gap-2 md:flex-row col-span-full bg-background p-4 md:p-6 border shadow-inner border-border rounded-xl",
				className,
			)}
		>
			<Button
				type="button"
				size={!isDekstop ? "default" : "sm"}
				variant="ghost"
				disabled={isLoading}
				onClick={(event) => {
					event.preventDefault();
					onBackRoute();
				}}
			>
				{taf("button.back")}
			</Button>

			<Button
				type="submit"
				size={!isDekstop ? "default" : "sm"}
				disabled={isLoading}
				className="order-first md:order-last"
			>
				{isLoading ? (
					<>
						<LoaderIcon className="animate-spin" />
						<span>{taf("button.save.loading")}</span>
					</>
				) : (
					<span>{taf("button.save.label")}</span>
				)}
			</Button>
		</div>
	);
});

// * for fields components
export interface RescheduleFieldsProps extends ComponentProps<"div"> {}

export const RescheduleFields = memo(function Component({
	className,
}: RescheduleFieldsProps) {
	const { t: tas } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule",
	});
	const { t: tasf } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.fields",
	});
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const formHooks = useRescheduleFields();
	const otherVisits = formHooks.apptDateTime.disabledVisits ?? [];

	// Get feature flag from page props
	const { props } = usePage<AppointmentRescheduleGlobalPageProps>();
	const useNewSchedulePicker = getUseNewSchedulePicker(props);
	const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("picker");
	// Changing the preferred gender clears the assigned therapist; confirm first
	// when one is already selected (a null value = no committed therapist).
	const [pendingGender, setPendingGender] = useState<string | null>(null);
	const applyGenderChange = (value: string) => {
		formHooks.form.setValue(
			"preferredTherapistGender",
			value as "MALE" | "FEMALE" | "NO PREFERENCE",
			{ shouldValidate: true },
		);
		formHooks.onResetAllTherapistState();
	};

	// The picker queries therapist availability by location + service + coords.
	// Prefer the visit-address snapshot; appointments created before that snapshot
	// existed fall back to the patient's current active address (same rule as bulk).
	// If neither is usable the picker cannot run at all, so we drop to the legacy
	// flow and hide the toggle entirely.
	const pickerProps = useMemo(() => {
		const visitAddress = resolveVisitAddress(
			formHooks.patientDetails,
			formHooks.appointment?.patient?.activeAddress,
		);
		if (!visitAddress) return null;

		return buildReschedulePickerProps(
			{ ...visitAddress, serviceId: formHooks.serviceId },
			{
				min: formHooks.apptDateTime.min,
				max: formHooks.apptDateTime.max,
			},
		);
	}, [
		formHooks.patientDetails,
		formHooks.appointment?.patient?.activeAddress,
		formHooks.serviceId,
		formHooks.apptDateTime,
	]);
	const showScheduleModeToggle = useNewSchedulePicker && !!pickerProps;
	const showPicker = showScheduleModeToggle && scheduleMode === "picker";

	const watchTherapistValue = useWatch({
		control: formHooks.form.control,
		name: "therapist",
	});
	const pickerSelection = useMemo(
		() =>
			toSlotSelection(
				watchTherapistValue,
				formHooks.watchAppointmentDateTimeValue,
			),
		[watchTherapistValue, formHooks.watchAppointmentDateTimeValue],
	);
	// Dates the order's OTHER visits already occupy — struck through in the
	// calendar. The server already excludes the visit being rescheduled and
	// emits ISO "YYYY-MM-DD".
	const otherScheduledDates = useMemo(
		() =>
			(formHooks.apptDateTime.disabledVisits ?? [])
				.map((visit) => visit.date)
				.filter((date): date is string => !!date),
		[formHooks.apptDateTime.disabledVisits],
	);
	const applyPickerSelection = useCallback(
		(selection: SlotSelection | null) => {
			if (selection) {
				const therapist = slotSelectionToTherapist(selection);
				formHooks.form.setValue("therapist", therapist, {
					shouldValidate: true,
				});
				formHooks.form.setValue(
					"appointmentDateTime",
					slotSelectionToDateTime(selection),
					{ shouldValidate: true },
				);
				// Keep formSelections in sync so the "Find therapist" tab reflects a
				// calendar pick — it reads formSelections, not the form field. The
				// slot only carries id/name/type, so the richer fields default; the
				// card shows the name, which is what matters here.
				formHooks.setFormSelections({
					therapist: {
						id: therapist.id,
						name: therapist.name,
						employmentType: therapist.employmentType,
						registrationNumber: "",
						employmentStatus: "ACTIVE",
					} as TherapistOption,
				});
				return;
			}

			formHooks.form.setValue("appointmentDateTime", null, {
				shouldValidate: false,
			});
			formHooks.onResetAllTherapistState();
		},
		[formHooks],
	);

	return (
		<>
			{/* Other scheduled visits — a dedicated section below Overview so the
			    order's occupied slots are visible before picking a new time. */}
			{otherVisits.length > 0 && (
				<div className="rounded-xl border border-border bg-background p-4 shadow-inner md:p-6">
					<div className="flex flex-wrap items-center gap-2">
						<CalendarClock className="size-4 shrink-0 text-muted-foreground" />
						<span className="font-bold text-muted-foreground text-xs uppercase tracking-wide">
							{tas("other_visits.title")}
						</span>
						<Badge
							variant="secondary"
							className="rounded-full px-2 py-0 text-[10px]"
						>
							{otherVisits.length}
						</Badge>
					</div>
					<p className="mt-1 text-muted-foreground text-xs">
						{tas("other_visits.hint")}
					</p>
					<div className="mt-3 flex flex-wrap gap-2">
						{otherVisits.map((v) => (
							<span
								key={v.visitNumber}
								className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5"
							>
								<span className="size-1.5 shrink-0 rounded-full bg-amber-500" />
								<span className="font-bold text-[10px] text-muted-foreground uppercase tracking-wide">
									V{v.visitNumber}
								</span>
								<span className="font-semibold text-sm">
									{format(new Date(v.date), "d MMM", { locale, in: tzDate })}
								</span>
								<span className="text-muted-foreground text-xs tabular-nums">
									·{" "}
									{format(
										parse(v.startTime, "HH:mm", new Date(), {
											locale,
											in: tzDate,
										}),
										timeFormatDateFns,
										{ locale, in: tzDate },
									)}
								</span>
							</span>
						))}
					</div>
				</div>
			)}

			<div
				className={cn(
					"grid grid-cols-1 gap-4 p-4 border shadow-inner md:grid-cols-2 md:p-6 border-border rounded-xl bg-background",
					className,
				)}
			>
				{!formHooks.therapistsOptions?.feasible?.length &&
					formHooks.isTherapistFound && (
						<Alert
							variant="destructive"
							className="col-span-full motion-preset-rebound-down"
						>
							<AlertCircle className="w-4 h-4 shrink-0" />
							<AlertTitle>{tas("alert_therapist.title")}</AlertTitle>
							<AlertDescription>
								{tas("alert_therapist.description")}
							</AlertDescription>
						</Alert>
					)}

				<div className="grid grid-cols-1 gap-4 col-span-full">
					<Deferred
						data={["optionsData"]}
						fallback={
							<div className="flex flex-col self-end gap-3 col-span-full">
								<Skeleton className="w-10 h-4 rounded-md" />
								<div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-1">
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
									<Skeleton className="relative w-full rounded-md h-9" />
								</div>
							</div>
						}
					>
						<FormField
							control={formHooks.form.control}
							name="preferredTherapistGender"
							render={({ field }) => (
								<FormItem className="space-y-3">
									<FormLabel>{tasf("pref_therapist_gender.label")}</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={(value) => {
												if (value === field.value) return;
												if (formHooks.formSelections?.therapist?.id) {
													setPendingGender(value);
													return;
												}
												applyGenderChange(value);
											}}
											value={field.value}
											orientation="horizontal"
											className="grid grid-cols-1 gap-4 md:grid-cols-3"
										>
											{formHooks.preferredTherapistGenderOption.map(
												(gender) => (
													<FormItem
														key={gender}
														className="flex items-start p-3 space-x-3 space-y-0 border rounded-md shadow-inner border-input bg-sidebar"
													>
														<FormControl>
															<RadioGroupItem value={gender} />
														</FormControl>
														<FormLabel className="flex items-center gap-1 font-normal uppercase truncate">
															{getGenderIcon(gender, "shrink-0")}
															<span className="truncate text-nowrap">
																{tasf(
																	`pref_therapist_gender.options.${gender.toLowerCase()}`,
																)}
															</span>
														</FormLabel>
													</FormItem>
												),
											)}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</Deferred>

					{showScheduleModeToggle ? (
						<ScheduleModeToggle
							value={scheduleMode}
							onChange={setScheduleMode}
						/>
					) : null}

					{/* In find-therapist mode the selected therapist sits at the top,
				    above the date/time picker — matching create booking. */}
					{!showPicker && formHooks.formSelections?.therapist?.name ? (
						<SelectedTherapistCard
							className="col-span-full"
							therapist={formHooks.formSelections.therapist}
							onUnselect={() =>
								formHooks.setFormSelections({
									...formHooks.formSelections,
									therapist: null,
								})
							}
						/>
					) : null}

					{showPicker && pickerProps ? (
						<div className="col-span-full">
							<SchedulePicker
								latitude={pickerProps.latitude}
								longitude={pickerProps.longitude}
								locationId={pickerProps.locationId}
								serviceId={pickerProps.serviceId}
								minDate={pickerProps.minDate}
								maxDate={pickerProps.maxDate}
								gender={formHooks.watchPreferredTherapistGenderValue}
								value={pickerSelection}
								onChange={applyPickerSelection}
								// Anchor = series lead (visit 1) therapist, resolved server-side
								// (not this visit's own therapist). Pulls a rescheduled visit
								// back toward the therapist who started the series.
								continuityTherapist={formHooks.continuityTherapist}
								// Rescheduling one visit must never reset the order's other visits.
								shouldConfirmTherapistChange={false}
								scheduledDates={otherScheduledDates}
								splitLayout
							/>
						</div>
					) : (
						<FormField
							control={formHooks.form.control}
							name="appointmentDateTime"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<DateTimePicker
											timeVariant="select"
											value={field.value}
											min={formHooks.apptDateTime.min}
											max={formHooks.apptDateTime.max}
											disabledVisits={formHooks.apptDateTime.disabledVisits}
											onChangeValue={field.onChange}
											isAllOfDay={!!formHooks.watchAllOfDayValue}
											allowPastTimes={!formHooks.apptDateTime.min}
											callbackOnChange={(newValue: Date | null) => {
												// Only reset therapist when date/time becomes null
												if (!newValue) {
													formHooks.onResetAllTherapistState();
												}
											}}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				{!showPicker && (
					<TherapistSearchList
						className="col-span-full"
						appointmentId={
							formHooks.appointment?.id
								? String(formHooks.appointment.id)
								: null
						}
						preferredTherapistGender={
							formHooks.watchPreferredTherapistGenderValue
						}
						isAllOfDay={!!formHooks.watchAllOfDayValue}
						selectedTherapistId={
							formHooks.formSelections?.therapist?.id ?? null
						}
						onSelect={(therapist) => {
							formHooks.setFormSelections({
								...formHooks.formSelections,
								therapist,
							});
							formHooks.onSelectTherapist(therapist);
						}}
					/>
				)}

				{/* Asked only while the order has no reason; otherwise the server reuses it. */}
				{props.mitraReason &&
					!orderMitraReason(props.mitraReason) &&
					isMitra(watchTherapistValue) && (
						<FormField
							control={formHooks.form.control}
							name="mitraAssignmentReason"
							render={({ field, fieldState }) => (
								<MitraAssignmentReasonBox
									id="mitra-reason"
									className="col-span-full"
									label={tasf("mitra_assignment_reason.label")}
									description={tasf("mitra_assignment_reason.description")}
									options={props.mitraReason?.options ?? []}
									value={field.value}
									onChange={field.onChange}
									error={fieldState.error?.message}
								/>
							)}
						/>
					)}

				<FormField
					control={formHooks.form.control}
					name="reason"
					render={({ field }) => (
						<FormItem className="col-span-full">
							<FormLabel>
								{tasf("reschedule_reason.label")}{" "}
								<span className="text-sm italic font-light">- (optional)</span>
							</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									placeholder={tasf("reschedule_reason.placeholder")}
									rows={3}
									className="shadow-inner bg-sidebar"
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={formHooks.form.control}
					name="sendEmailNotification"
					render={({ field }) => (
						<FormItem className="flex h-full flex-row items-center justify-between gap-3 p-3 border rounded-md shadow-inner border-input bg-sidebar col-span-full">
							<div className="space-y-0.5">
								<FormLabel>{tasf("send_email_notification.label")}</FormLabel>
								<p className="text-xs text-muted-foreground text-pretty">
									{tasf("send_email_notification.description")}
								</p>
							</div>
							<FormControl>
								<Switch
									className="!mt-0"
									checked={field.value}
									onCheckedChange={field.onChange}
								/>
							</FormControl>
						</FormItem>
					)}
				/>

				<BypassChecksPanel>
					<FormField
						control={formHooks.form.control}
						name="bypassSlotConflict"
						render={({ field }) => (
							<BypassCheckRow
								id="bypass-slot-conflict"
								keyPrefix="bypass_slot_conflict"
								checked={field.value}
								onCheckedChange={field.onChange}
							/>
						)}
					/>
					<FormField
						control={formHooks.form.control}
						name="bypassContractCheck"
						render={({ field }) => (
							<BypassCheckRow
								id="bypass-contract-check"
								keyPrefix="bypass_contract_check"
								checked={field.value}
								onCheckedChange={field.onChange}
							/>
						)}
					/>
					<FormField
						control={formHooks.form.control}
						name="bypassJourneyCheck"
						render={({ field }) => (
							<BypassCheckRow
								id="bypass-journey-check"
								keyPrefix="bypass_journey_check"
								checked={field.value}
								onCheckedChange={field.onChange}
							/>
						)}
					/>
				</BypassChecksPanel>

				{/* for showing the alert error if there's any server validation error */}
				{!!formHooks.errorsServerValidation?.length && (
					<Alert
						variant="destructive"
						className="col-span-full motion-preset-rebound-down"
					>
						<AlertCircle className="size-4" />
						<AlertTitle className="text-xs">Error</AlertTitle>
						<AlertDescription className="text-xs">
							<ul className="list-disc">
								{formHooks.errorsServerValidation?.map((error) => (
									<li key={error}>{error}</li>
								))}
							</ul>
						</AlertDescription>
					</Alert>
				)}

				<AlertDialog
					open={!!pendingGender}
					onOpenChange={(open) => {
						if (!open) setPendingGender(null);
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{tas("schedule_picker.common.gender_confirm_title")}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{tas("schedule_picker.common.gender_confirm_desc")}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={() => setPendingGender(null)}>
								{tas("schedule_picker.common.cancel")}
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => {
									if (pendingGender) applyGenderChange(pendingGender);
									setPendingGender(null);
								}}
							>
								{tas("schedule_picker.common.continue")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</>
	);
});
